import type { Product, Variant } from '../../types'
import { canonicalPath } from './seo'

export interface SsrVariantChipsProps {
  lang: string
  product: Product
  variants: Variant[]
  selected: Variant | null
}

/**
 * SSR-friendly variant selector. Renders each variant as a crawlable `<a>`
 * tag that navigates to its own URL — the chip click causes a full page
 * load, so the browser lands on a freshly SSR-rendered page with the
 * correct title, canonical, JSON-LD, and price.
 *
 * Grouped by `variant_type` to mirror the CSR VariantSelector layout.
 */
export default function SsrVariantChips({ lang, product, variants, selected }: SsrVariantChipsProps) {
  const groups = new Map<string, Variant[]>()
  for (const v of variants) {
    const key = v.variant_type || 'variant'
    const list = groups.get(key)
    if (list) list.push(v)
    else groups.set(key, [v])
  }
  const entries = Array.from(groups.entries())
  const showHeaders = entries.length > 1

  return (
    <div className="py-2" aria-label="Kies een uitvoering" data-testid="variant-chips">
      {entries.map(([type, list]) => (
        <div key={type} className="mb-3 last:mb-0">
          <p className="text-xs font-medium text-neutral-600 uppercase tracking-wider mb-2">
            {showHeaders ? groupLabel(type) : 'Uitvoering'}
          </p>
          <div className="flex flex-wrap gap-2" role="list">
            {list.map((v) => {
              const isSelected = v.uuid === selected?.uuid
              // Selecting the default variant points at the canonical product
              // URL; everything else points at the per-variant URL.
              const href =
                v.is_default && isSelected
                  ? canonicalPath(lang, product.slug)
                  : canonicalPath(lang, product.slug, v.slug)
              return (
                <a
                  key={v.uuid}
                  href={href}
                  role="listitem"
                  aria-current={isSelected ? 'true' : undefined}
                  data-variant-slug={v.slug}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                    isSelected
                      ? 'border-primary bg-gcg-tint text-primary ring-1 ring-primary'
                      : 'border-neutral-200 bg-white text-neutral-700 hover:border-neutral-400 hover:bg-neutral-50'
                  }`}
                >
                  {v.variant_label || v.normalized_mpn}
                </a>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

function groupLabel(type: string): string {
  const labels: Record<string, string> = {
    length: 'Lengte',
    color: 'Kleur',
    coating: 'Coating',
    voltage: 'Spanning',
    temperature: 'Temperatuur',
    variant: 'Uitvoering',
  }
  return labels[type] ?? type.replace(/_/g, ' ')
}
